import { useState, useEffect } from 'react'
import { useMutation } from "@tanstack/react-query"

import { showLoading } from 'services/util'

import { graphql } from '../api'
import { updateAuctionMutation } from '../graphql/Mutations'

import themeCss from '../components/sass/theme.module.scss'
import adminCss from './sass/adminpage.module.scss'

import ConfirmStatusUpdate from './ConfirmModal/ConfirmStatusUpdate'
import { areAllChargesPaid, sendAllPaymentReminderEmails } from './auctionsadminutil'
import { canEditAuction, isAuctionOwner } from './permissionsutil'

export default function UpdateAuctionStatusButton({ auction, fromStatus, toStatus, refreshAdminAuctionQuery, needsSolicitation}) {

  const [showModal, setShowModal] = useState(false)
  const [buttonText, setButtonText] = useState('Update Status')
  const [headerText, setHeaderText] = useState('')
  const [paragraphText, setParagraphText] = useState('')
  const [triggerLoading, setTriggerLoading] = useState(false)

  const [hasUnsavedAuctionChanges, setHasUnsavedAuctionChanges] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)

  //initialize it in the useState, if it's for ending the auction, it will update in the useEffect
  const [input, setInput] = useState({
    AuctionId: auction.AuctionId,
    Status: toStatus
  })
  
  const {data: updateAuctionResponse, error, isPending, mutate: saveAuction } = useMutation({
    mutationFn: (input) => {
      setTriggerLoading(true)
      const updateAuctionMutationResponse = graphql({query: updateAuctionMutation, variables: {input} })
      // console.log({updateAuctionMutationResponse})
      return updateAuctionMutationResponse
    },
  })

  useEffect(() => {
    const handleUnsavedChange = () => {
      const unsaved = sessionStorage.getItem('unsavedAuction')
      setHasUnsavedAuctionChanges(unsaved && unsaved !== 'null')
    }
    
    handleUnsavedChange() // Initial check
    window.addEventListener('session-storage-change', handleUnsavedChange)
    
    return () => window.removeEventListener('session-storage-change', handleUnsavedChange)
  }, [])

  useEffect(() => {

    // trigger loading was needed because in sister components (inside the tabs) the showLoading would get overriden by the update auction status button loading
    // set up an internal check to see if the component itself needed to show loading,
    // and only affect the showLoading util function if it was being caused by THIS component
    const performErrorCheck = () => {
      if (error) {
        console.log("Auction not created: ", error)
        console.log(error.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
      }
      else if (updateAuctionResponse) {
        console.log(updateAuctionResponse)
        console.log('Auction updated: ', updateAuctionResponse.data.updateAuction.AuctionId)
        //invalidate query to refresh the page from AdminAuctionTabs.js
        refreshAdminAuctionQuery()
        setShowModal(false)
      }
    }

    if (triggerLoading) {
      showLoading(isPending)
  
      performErrorCheck()

      setTriggerLoading(false)
      showLoading(isPending)

    } else {
      performErrorCheck()
    }
  }, [updateAuctionResponse, error, isPending, refreshAdminAuctionQuery, triggerLoading])

  useEffect(() => {
    switch(fromStatus) {
      case "PREVIEW":
        setHeaderText('Are you sure you want to set this auction to Public?')
        setParagraphText('Making an event public will create an event in Event Hub, allow registrations, and it will be accessible by anyone with the link.')
        setButtonText('Make Auction Public')
        break;
      case "PUBLIC":
        if (toStatus === 'ENDED') {
          let now = new Date()
          setHeaderText('Are you sure you want to end the auction?')
          setParagraphText('Bidding results will be saved as is. Attendees will no longer be able to make donations or purchase any more Fixed Price Items. This action cannot be undone.')
          setInput({
            AuctionId: auction.AuctionId,
            EndTime: now.toISOString(),
            IsEndNow: true,
          })
          setButtonText('End Auction')
        }
        if (toStatus === 'CLOSED') {
          setHeaderText('Are you sure you want to archive this auction site?')
          setParagraphText('Attendees will no longer be able to access the site for this auction. This action cannot be undone.')
          setButtonText('Archive Auction')
        }
        if (toStatus === 'PREVIEW') {
          setHeaderText('Are you sure you want to set this auction to Preview?')
          setParagraphText('Once the auction is set to Preview, it is no longer be accessible by the public.')
          setButtonText('Set To Preview')
        }
        break;
      case "CLOSED":
        setHeaderText('TESTING ONLY')
        setParagraphText('Only for testing to move an auction to Public status. Do not do in production.')
        setButtonText('TESTING ONLY TO PUBLIC')
        break;
      default:
        setButtonText('Error Loading State')
    }
  }, [toStatus, fromStatus, auction.AuctionId])
  
  useEffect(() => {
    // console.log({hasUnsavedAuctionChanges, needsSolicitation, toStatus, auction})
    const shouldButtonBeDisabled = () => {
      if (hasUnsavedAuctionChanges) return true
      if (toStatus === 'CLOSED' && areAllChargesPaid(auction) === false) return true
      if (needsSolicitation === undefined) return false
      else return needsSolicitation
    }
    setIsButtonDisabled(shouldButtonBeDisabled())
  }, [hasUnsavedAuctionChanges, needsSolicitation, toStatus, auction])
  
  
  if (isAuctionOwner(auction)) {
    //auction owner can see all status change options
  }
  //if you can edit the auction, you can only end bidding, no other status changes
  else if (canEditAuction(auction)) {
    const buttonWillEndBidding = (fromStatus === "PUBLIC" && toStatus === "ENDED")
    //if the button does not end bidding, you cannot see it
    if (!buttonWillEndBidding) return
  }
  
  // if you cannot edit - you cannot see anything
  if (!canEditAuction(auction)) return

  return (
    <>
      {(fromStatus !== "CLOSED" && toStatus !== "PREVIEW") && 
        <button className={`${themeCss['button']}
          ${toStatus === 'CLOSED' ? themeCss['FilledRed'] :themeCss['FilledBlue']} 
        ${buttonText === 'End Auction' ? themeCss['StrokeRed'] : ''}`} onClick={() => setShowModal(true)}
        // disabled={needsSolicitation === undefined ? false : needsSolicitation}>
        disabled={isButtonDisabled}>
        {buttonText}
      </button>}
      {(hasUnsavedAuctionChanges && auction.ReviewStatus === 'Approved') && <p className={adminCss['solicitation-warning-nonadmin']}>Save or discard auction changes before updating state.</p>}

      {showModal && 
        <ConfirmStatusUpdate setShowConfirmModal={setShowModal} saveAuction={saveAuction}
          headerText={headerText} paragraphText={paragraphText} buttonText={`Confirm ${buttonText}`} input={input} /> }
    </>
  )
}